"use client";

import type React from "react";
import { useState } from "react";
import { motion } from "framer-motion";
import { X, Shield, RefreshCw } from "lucide-react";
import { useAuthStore } from "../store/authStore";

interface VerificationModalProps {
  onClose: () => void;
}

const VerificationModal: React.FC<VerificationModalProps> = ({ onClose }) => {
  const [code, setCode] = useState("");
  const { verify2FA, resendCode, isLoading } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await verify2FA(code);
    if (success) {
      onClose();
    }
  };

  const handleResend = async () => {
    console.log("Resending code...");
    await resendCode();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="glass-effect rounded-2xl p-6 w-full max-w-md"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Shield className="w-6 h-6 text-blue-400 mr-2" />
            <h2 className="text-xl font-semibold text-white">
              Two-Factor Authentication
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <p className="text-gray-300 mb-6">
          We've sent a verification code to your email. Please enter it below to
          complete your login.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Verification Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-center text-2xl tracking-widest"
              placeholder="000000"
              maxLength={6}
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="w-full banking-gradient py-3 px-4 rounded-lg text-white font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              "Verify & Login"
            )}
          </button>

          <button
            type="button"
            onClick={handleResend}
            className="w-full text-blue-400 hover:text-blue-300 py-2 transition-colors flex items-center justify-center"
          >
            <RefreshCw size={16} className="mr-2" />
            Resend Code
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default VerificationModal;
